import { z } from 'zod';
import { dateOnlySchema, paginationSchema } from './common.js';

/** Ein Gerät gehört entweder einer Person, einem Standort oder niemandem. */
export const holderTypeSchema = z.enum(['employee', 'location']);
export type HolderType = z.infer<typeof holderTypeSchema>;

export const holderSchema = z.object({
  type: holderTypeSchema,
  id: z.number().int(),
  name: z.string(),
});

const refSchema = z.object({ id: z.number().int(), name: z.string() });

export const assetSchema = z.object({
  id: z.number().int(),
  asset_tag: z.string(),
  serial: z.string(),
  model: refSchema.nullable(),
  category: refSchema.nullable(),
  manufacturer: refSchema.nullable(),
  status: z.object({
    id: z.number().int(),
    name: z.string(),
    is_archived: z.boolean(),
  }),
  holder: holderSchema.nullable(),
  home_location: refSchema.nullable(),
  /**
   * Wo das Gerät ist — abgeleitet, nicht gespeichert.
   *
   * Ein Gerät bei einer Person ist **beim Mitarbeiter**, und das ist die
   * genaueste Aussage, die es gibt: Die Leute arbeiten von überall, ein
   * Bürostandort wäre hier eine Scheingenauigkeit. Hat die Person einen festen
   * Standort hinterlegt, wird der zusätzlich genannt.
   *
   * `source` sagt, worauf die Angabe beruht — Erschlossenes soll nicht wie
   * Gesichertes aussehen.
   */
  current_location: z
    .object({
      name: z.string(),
      source: z.enum([
        'holder_location', // an einen Standort ausgegeben
        'with_employee', // bei einer Person, ortsungebunden
        'employee_location', // bei einer Person mit festem Standort
        'home_location', // liegt am hinterlegten Standort
        'unknown', // keine Angabe vorhanden
      ]),
      /** Bei einer Person: deren Name — für die Anzeige „bei Astrid Blatt". */
      holder_name: z.string().nullable().optional(),
    })
    .nullable(),
  supplier: refSchema.nullable(),
  purchase_date: z.string().nullable(),
  purchase_cost: z.string().nullable(),
  order_number: z.string().nullable(),
  /** Verknüpfung zum Anlagengut in Datev — von Khadi pflegbar. */
  datev_id: z.string().nullable(),
  notes: z.string().nullable(),
  label_printed_at: z.string().nullable(),
  snipe_id: z.number().int().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Asset = z.infer<typeof assetSchema>;

/** Zuweisungsziel — in Formularen und in der API identisch aufgebaut. */
export const holderInputSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('employee'), id: z.number().int().positive() }),
  z.object({ type: z.literal('location'), id: z.number().int().positive() }),
  z.object({ type: z.literal('none') }),
]);
export type HolderInput = z.infer<typeof holderInputSchema>;

export const assetCreateSchema = z.object({
  serial: z.string().trim().min(1, 'Seriennummer ist erforderlich').max(120),
  model_id: z.number().int().positive(),
  /** Weglassen = nächste freie Nummer. Nur der Admin überschreibt das. */
  asset_tag: z.string().trim().max(32).optional(),
  status_id: z.number().int().positive().optional(),
  holder: holderInputSchema.optional(),
  home_location_id: z.number().int().positive().nullable().optional(),
  supplier_id: z.number().int().positive().nullable().optional(),
  purchase_date: dateOnlySchema.nullable().optional(),
  purchase_cost: z.number().nonnegative().nullable().optional(),
  order_number: z.string().trim().max(120).nullable().optional(),
  datev_id: z.string().trim().max(60).nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
});
export type AssetCreate = z.infer<typeof assetCreateSchema>;

export const assetUpdateSchema = assetCreateSchema.partial().omit({ holder: true });
export type AssetUpdate = z.infer<typeof assetUpdateSchema>;

/** Massenerfassung: gemeinsames Modell und Ziel, viele Seriennummern. */
export const assetBulkCreateSchema = z.object({
  serials: z.array(z.string().trim().min(1)).min(1).max(200),
  model_id: z.number().int().positive(),
  status_id: z.number().int().positive().optional(),
  holder: holderInputSchema.optional(),
  home_location_id: z.number().int().positive().nullable().optional(),
  supplier_id: z.number().int().positive().nullable().optional(),
  purchase_date: dateOnlySchema.nullable().optional(),
  purchase_cost: z.number().nonnegative().nullable().optional(),
  order_number: z.string().trim().max(120).nullable().optional(),
  /** Bereits vorhandene Seriennummern überspringen statt abbrechen. */
  skip_existing: z.boolean().default(true),
  dry_run: z.boolean().default(false),
});

/**
 * Übergeben, zurücknehmen, ausmustern — ein Endpunkt.
 * Der Status folgt der Zuweisung automatisch; `status_id` wird nur
 * beim Ausmustern mitgegeben.
 */
export const assetMoveSchema = z.object({
  to: holderInputSchema,
  status_id: z.number().int().positive().optional(),
  note: z.string().trim().max(1000).optional(),
  occurred_at: z.string().datetime().optional(),
  /** Empfangsbestätigung per Mail anfordern. */
  request_confirmation: z.boolean().default(false),
});
export type AssetMove = z.infer<typeof assetMoveSchema>;

export const assetSortSchema = z
  .enum([
    'asset_tag',
    'serial',
    'model',
    'status',
    'holder',
    'purchase_date',
    'created_at',
    'updated_at',
  ])
  .default('asset_tag');

export const assetListQuerySchema = paginationSchema.extend({
  /** Volltext über Tag, Seriennummer, Modell, Halter und Notizen. */
  q: z.string().trim().max(200).optional(),
  status_id: z.coerce.number().int().positive().optional(),
  category_id: z.coerce.number().int().positive().optional(),
  manufacturer_id: z.coerce.number().int().positive().optional(),
  model_id: z.coerce.number().int().positive().optional(),
  location_id: z.coerce.number().int().positive().optional(),
  holder_type: holderTypeSchema.optional(),
  holder_id: z.coerce.number().int().positive().optional(),
  employee_id: z.coerce.number().int().positive().optional(),
  /** Archivierte (verschrottet, verkauft) sind standardmäßig ausgeblendet. */
  include_archived: z.coerce.boolean().default(false),
  only_archived: z.coerce.boolean().default(false),
  unassigned: z.coerce.boolean().optional(),
  /** Geräte mit oder ohne Datev-Verknüpfung — für die Anlagenpflege. */
  has_datev_id: z.coerce.boolean().optional(),
  /** Nach abgeleitetem Aufenthaltsort filtern (Name des Standorts). */
  at_location: z.string().trim().max(160).optional(),
  /** Nur Geräte, deren Aufenthaltsort unbekannt ist. */
  location_unknown: z.coerce.boolean().optional(),
  sort: assetSortSchema,
  order: z.enum(['asc', 'desc']).default('asc'),
});
export type AssetListQuery = z.infer<typeof assetListQuerySchema>;

/** Scanner: beliebiger Code — URL, ID, Tag oder Seriennummer. */
export const assetLookupParamsSchema = z.object({
  code: z.string().trim().min(1).max(500),
});
